import { NgIf, NgClass, NgFor, AsyncPipe } from '@angular/common';
import { Component, OnDestroy, inject } from '@angular/core';
import { CoreSelectors } from '@next/core-lib';
import { RootState } from '@next/core-lib/app';
import { BrandingService } from '@next/core-lib/branding';
import { PrintPageComponent } from '@next/core-lib/components';
import {
  AgrCurrencyPipe,
  AgrNumberPipe,
  LocaleService,
  CurrencySymbolPipe,
  TranslatePipe,
  AgrDatePipe,
} from '@next/core-lib/i18n';
import { Customer, UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { delay, filter, map, skipWhile, take, withLatestFrom } from 'rxjs/operators';
import { BrandingLogoSrc } from 'src/app/branding/branding.model';
import {
  BalancePerDeliveryLine,
  Barn,
  BarnBalance,
  Cycle,
  DayOldChick,
  Departure,
  Discount,
} from 'src/app/cycle/models/cycle.model';
import { CycleService } from 'src/app/cycle/services/cycle.service';
import { getCycles } from 'src/app/cycle/store/selectors/cycle.selectors';

import { NormalizedFlockDataObject } from '../../models/flock-report-service.model';
import { ReportSelection, SummarizedBalanceRow } from '../../models/report.model';
import { getNormalizedFlockData, getSelectedReport } from '../../store/selectors/report.selectors';

@Component({
  selector: 'app-report-print',
  templateUrl: './report-print.component.html',
  styleUrls: ['./report-print.component.scss'],
  imports: [
    NgIf,
    PrintPageComponent,
    NgClass,
    NgFor,
    AsyncPipe,
    CurrencySymbolPipe,
    TranslatePipe,
    AgrDatePipe,
  ],
})
export class ReportPrintComponent implements OnDestroy {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly localeService = inject(LocaleService);
  private readonly decimalPipe = inject(AgrNumberPipe);
  private readonly currencyPipe = inject(AgrCurrencyPipe);
  private readonly cycleService = inject(CycleService);
  private readonly brandingService = inject(BrandingService);

  normalizedFlockDataObject$: Observable<NormalizedFlockDataObject> =
    this.store.select(getNormalizedFlockData);

  private readonly COUNTRIES_WITHOUT_FINANCIALS = ['PL'];
  selectedReport$ = this.store.select(getSelectedReport);
  customer$: Observable<Customer> = this.store.select(UserSelectors.selectCustomer);
  barns: Barn[] = [];
  summarizedBalanceRows: SummarizedBalanceRow[] = [];
  feedBalancePrice?: number;
  feedBalancePriceExclDiscounts?: number;
  totalConsumptionPerDayOldChick?: string;
  reportSubscription: Subscription;

  isiOS: boolean = this.iOS();
  showAgrifirmLogo = this.brandingService.isTenant('Agrifirm');
  customizedLogoSrc: string = BrandingLogoSrc.PoultryNextLogo;

  cycle$: Observable<Cycle> = this.selectedReport$.pipe(
    withLatestFrom(this.store.select(getCycles)),
    map(([reportSelection, cycles]) => {
      let cycle = cycles.find((c) => c.id === reportSelection?.cycleId);
      if (cycle) {
        this.barns = this.getBarnsByCycle(cycle);
        cycle = {
          ...cycle,
          dayOldChicks: reportSelection.barnId
            ? cycle.dayOldChicks
                .filter((d) => d.barn?.id === reportSelection.barnId)
                .map(
                  (dayOldChick) =>
                    ({
                      ...dayOldChick,
                      chicksTotalPrice: Number.isFinite(dayOldChick.chicksPrice)
                        ? dayOldChick.amountOfChicks * dayOldChick.chicksPrice
                        : undefined,
                    }) as DayOldChick,
                )
            : this.aggregateDayOldChicks(cycle.dayOldChicks),
          departures: reportSelection.barnId
            ? cycle.departures.filter((d) => d.barn?.id === reportSelection.barnId)
            : cycle.departures,
          balancesPerDeliveryLine: cycle.balancesPerDeliveryLine.map((balance) => ({
            ...balance,
            barnBalances: balance.barnBalances.filter((b) =>
              reportSelection.barnId ? b.barnId === reportSelection.barnId : true,
            ),
          })),
        };
      }
      return cycle;
    }),
  );

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    if (this.brandingService.isTenant('Agravis')) {
      this.customizedLogoSrc = BrandingLogoSrc.AgravisLogo;
      this.showAgrifirmLogo = false;
    }

    this.reportSubscription = this.normalizedFlockDataObject$
      .pipe(
        withLatestFrom(
          this.store.select(CoreSelectors.selectLoading).pipe(
            filter((isLoading) => !isLoading),
            take(1),
          ),
        ),
        withLatestFrom(
          this.cycle$.pipe(
            filter((cycle) => cycle !== undefined && cycle !== null),
            take(1),
          ),
        ),
        delay(300),
      )
      .subscribe(([[normalizedFlockData, loading], cycle]) => {
        const existingTitle = document.title;
        const proposedTitle =
          this.localeService.translate('report-name') +
          new Date().toLocaleDateString(this.localeService.getLocale());
        if (window && normalizedFlockData && !loading && cycle) {
          document.title = proposedTitle;
          window.print();
          document.title = existingTitle;
        }
      });

    this.normalizedFlockDataObject$
      .pipe(
        withLatestFrom(this.cycle$),
        withLatestFrom(this.selectedReport$),
        skipWhile(
          ([[normalizedFlockData, cycle], selectedReport]) =>
            normalizedFlockData === undefined ||
            cycle === undefined ||
            selectedReport === undefined,
        ),
      )
      .subscribe(([[normalizedFlockData, cycle], selectedReport]) => {
        if (
          normalizedFlockData !== undefined &&
          cycle !== undefined &&
          selectedReport !== undefined
        ) {
          this.summarizedBalanceRows = this.getSummarizedBalanceRows(
            cycle,
            normalizedFlockData.dayOldChicks,
          );
          this.feedBalancePrice = normalizedFlockData.feedBalancePrice;
          this.feedBalancePriceExclDiscounts = normalizedFlockData.feedBalancePriceExclDiscounts;
          this.totalConsumptionPerDayOldChick = this.getTotalConsumptionPerDayOldChick(
            cycle.balancesPerDeliveryLine,
            selectedReport,
            normalizedFlockData.dayOldChicks,
          );
        }
      });
  }

  ngOnDestroy(): void {
    if (this.reportSubscription) {
      this.reportSubscription.unsubscribe();
    }
  }

  getDiscounts(cycle: Cycle, applicabilityType: string, barnId: number): Discount[] {
    return cycle.discounts
      ? cycle.discounts.filter((discount) =>
          barnId
            ? discount.applicabilityType === applicabilityType && discount.barn.id === barnId
            : discount.applicabilityType === applicabilityType,
        )
      : [];
  }

  getFeedPricePerDeliveredKg(totalFeedPrice: number, totalDeliveredKg: number) {
    return (totalFeedPrice / totalDeliveredKg) * 100;
  }

  getSummarizedBalanceRows(cycle: Cycle, totalDayOldChicks: number): SummarizedBalanceRow[] {
    const summarizedBalanceRows: SummarizedBalanceRow[] = [];
    cycle.balancesPerDeliveryLine.forEach((balanceRow) => {
      let existing = summarizedBalanceRows.find(
        (s) =>
          s.productIdentifier === balanceRow.productIdentifier &&
          s.productTitle === balanceRow.productTitle,
      );

      if (!existing) {
        existing = {
          productTitle: balanceRow.productTitle,
          productIdentifier: balanceRow.productIdentifier,
          consumptionPerDayOldChick: 0,
          totalConsumption: 0,
          totalPrice: 0,
          currency: balanceRow.currency,
        };
        summarizedBalanceRows.push(existing);
      }

      const totalBarnConsumption = balanceRow.barnBalances.reduce(
        (sum, current) => sum + current.amount,
        0,
      );
      existing.totalConsumption += totalBarnConsumption;
      existing.totalPrice += this.cycleService.calculateBalancePriceForTotalConsumption(
        balanceRow.nettoAmount,
        balanceRow.baseQuantity,
        totalBarnConsumption,
      );
      existing.consumptionPerDayOldChick = (existing.totalConsumption / totalDayOldChicks) * 1000.0;
    });
    return summarizedBalanceRows;
  }

  getBarnsByCycle(cycle: Cycle) {
    const barns: Barn[] = [];
    cycle.dayOldChicks.forEach((d) => {
      if (!barns.some((b) => b.id === d.barn.id)) {
        barns.push(d.barn);
      }
    });
    return barns.sort((a, b) => (a.name > b.name ? 1 : -1));
  }

  getBarnNames(selectedReport: ReportSelection) {
    if (selectedReport.barnId) {
      return this.barns.find((b) => b.id === selectedReport.barnId)?.name;
    }
    return this.barns.map((b) => b.name).join(', ');
  }

  getValue(value: number, digitsInfo = '1.0-2') {
    if (value === undefined || value === null || Number.isNaN(value) || !Number.isFinite(value)) {
      return this.localeService.translate('n/a');
    }
    return this.decimalPipe.transform(value, digitsInfo);
  }

  getCurrencyValue(value: number, currencyCode: string) {
    if (value === undefined || value === null || Number.isNaN(value) || !Number.isFinite(value)) {
      return this.localeService.translate('n/a');
    }
    return this.currencyPipe.transform(value, currencyCode);
  }

  //#region Day Old Chicks

  getChickAge(dayOldChick: DayOldChick) {
    if (
      !dayOldChick.arrivalDate ||
      dayOldChick.arrivalDate === 'NaN' ||
      !dayOldChick.chickBirthDate ||
      dayOldChick.chickBirthDate === 'NaN'
    ) {
      return this.localeService.translate('n/a');
    }

    const arrivalDate = new Date(dayOldChick.arrivalDate);
    const chickBirthDate = new Date(dayOldChick.chickBirthDate);

    const time = arrivalDate.getTime() - chickBirthDate.getTime();
    const days = time / (1000 * 3600 * 24);

    return this.getValue(days);
  }

  getTotalDepartureWeight(cycle: Cycle) {
    return cycle?.departures.reduce((sum, current) => sum + current.departureWeight, 0);
  }

  getTotalSlaughterWeight(cycle: Cycle) {
    return cycle?.departures.reduce((sum, current) => sum + current.slaughterWeight, 0);
  }

  getChicksPrice(dayOldChick: DayOldChick, currencyCode: string) {
    if (!dayOldChick.chicksTotalPrice || isNaN(dayOldChick.chicksTotalPrice)) {
      return this.localeService.translate('n/a');
    }

    const chicksPriceAfterChickDeliveryDiscount =
      this.getChicksPriceAfterChickDeliveryDiscount(dayOldChick);
    return this.getCurrencyValue(chicksPriceAfterChickDeliveryDiscount, currencyCode);
  }

  getChicksPriceAfterChickDeliveryDiscount(dayOldChick: DayOldChick): number {
    const chicksDeliveryDiscount = dayOldChick.discountPricePerChick
      ? dayOldChick.discountPricePerChick * dayOldChick.amountOfChicks
      : 0;
    return dayOldChick.chicksTotalPrice - chicksDeliveryDiscount;
  }

  //#endregion Day Old Chicks

  //#region Departures

  getDepartureChickAge(departure: Departure, dayOldChicks: DayOldChick[]) {
    const averageBirthDate = this.getWeightedAverageBirthDate(dayOldChicks);
    const averageDepartureDate = new Date(departure.departureDate);

    const time = averageDepartureDate.getTime() - averageBirthDate.getTime();
    const days = time / (1000 * 3600 * 24);

    return this.getValue(days);
  }

  private getWeightedAverageBirthDate(dayOldChicks: DayOldChick[]) {
    const averageBirthDate =
      dayOldChicks.reduce(
        (sum, current) => sum + new Date(current.chickBirthDate).getTime() * current.amountOfChicks,
        0,
      ) / dayOldChicks.reduce((sum, current) => sum + current.amountOfChicks, 0);
    const weightedAverageBirthDate = new Date(averageBirthDate);

    return weightedAverageBirthDate;
  }

  getDepartureAverageChickWeight(departure: Departure) {
    if (
      !departure.departureWeight ||
      isNaN(departure.departureWeight) ||
      !isFinite(departure.departureWeight) ||
      !departure.amountOfChicks ||
      isNaN(departure.amountOfChicks) ||
      !isFinite(departure.amountOfChicks)
    ) {
      return this.localeService.translate('n/a');
    }

    return this.getValue((departure.departureWeight / departure.amountOfChicks) * 1000, '1.0-0');
  }

  getRejectPercentage(departure: Departure) {
    if (
      !departure.rejectionAtSlaughterhouseInKg ||
      isNaN(departure.rejectionAtSlaughterhouseInKg) ||
      !isFinite(departure.rejectionAtSlaughterhouseInKg) ||
      !departure.departureWeight ||
      isNaN(departure.departureWeight) ||
      !isFinite(departure.departureWeight)
    ) {
      return this.localeService.translate('n/a');
    }

    return this.getValue(
      (departure.rejectionAtSlaughterhouseInKg / departure.departureWeight) * 100,
    );
  }
  //#endregion Departures

  //#region Balance

  getConsumption(barnBalances: BarnBalance[], reportSelection: ReportSelection) {
    if (reportSelection.barnId) {
      return this.getValue(barnBalances.find((b) => b.barnId === reportSelection.barnId).amount);
    }
    return this.getValue(barnBalances.reduce((sum, current) => sum + current.amount, 0));
  }

  getConsumptionPerDayOldChick(
    barnBalances: BarnBalance[],
    reportSelection: ReportSelection,
    totalDayOldChicksInBarn: number,
  ) {
    const digitsInfo = '1.0-0';
    if (reportSelection.barnId) {
      return this.getValue(
        (barnBalances.find((b) => b.barnId === reportSelection.barnId).amount /
          totalDayOldChicksInBarn) *
          1000,
        digitsInfo,
      );
    }
    return this.getValue(
      (barnBalances.reduce((sum, current) => sum + current.amount, 0) / totalDayOldChicksInBarn) *
        1000,
      digitsInfo,
    );
  }

  getTotalConsumptionPerDayOldChick(
    balance: BalancePerDeliveryLine[],
    reportSelection: ReportSelection,
    totalDayOldChicksInBarn: number,
  ) {
    const digitsInfo = '1.0-0';
    if (reportSelection.barnId) {
      let sum = 0;

      balance.forEach((bpdl) => {
        if (bpdl.barnBalances.length > 0) {
          sum +=
            (bpdl.barnBalances.find((b) => b.barnId === reportSelection.barnId).amount /
              totalDayOldChicksInBarn) *
            1000;
        }
      });
      return this.getValue(sum, digitsInfo);
    }
    let sum = 0;

    balance.forEach((bpdl) => {
      if (bpdl.barnBalances.length > 0) {
        sum +=
          (bpdl.barnBalances.reduce((sum, current) => sum + current.amount, 0) /
            totalDayOldChicksInBarn) *
          1000;
      }
    });
    return this.getValue(sum, digitsInfo);
  }

  //#endregion Balance

  //#region Technical
  getWeight(weight: number) {
    if (!weight || isNaN(weight) || !isFinite(weight)) {
      return this.localeService.translate('n/a');
    }

    return this.getValue(weight * 1000);
  }
  //#endregion Technical

  //#region Financial

  showPrices$: Observable<boolean> = this.customer$.pipe(
    map(
      (customer) => this.COUNTRIES_WITHOUT_FINANCIALS.indexOf(customer.country.toUpperCase()) < 0,
    ),
  );

  showFinancials$(normalizedFlockData: NormalizedFlockDataObject): Observable<boolean> {
    return this.showPrices$.pipe(
      map(
        (showPrices) =>
          showPrices &&
          (isFinite(normalizedFlockData.dayOldChicksPrice) ||
            isFinite(normalizedFlockData.feedConsumptionPricePer100Kg) ||
            (isFinite(normalizedFlockData.feedBalancePrice) &&
              normalizedFlockData.feedBalancePrice > 0) ||
            isFinite(normalizedFlockData.nettoRevenue) ||
            isFinite(normalizedFlockData.nettoRevenuePerChick) ||
            isFinite(normalizedFlockData.nettoRevenuePerSquareMeterPerDay) ||
            isFinite(normalizedFlockData.nettoProfitInclOtherCosts)),
      ),
    );
  }

  getPricePerDeliveredChick(dayOldChicksPrice: number, amountOfChicks: number) {
    if (
      !dayOldChicksPrice ||
      isNaN(dayOldChicksPrice) ||
      !isFinite(dayOldChicksPrice) ||
      !amountOfChicks ||
      isNaN(amountOfChicks) ||
      !isFinite(amountOfChicks)
    ) {
      return this.localeService.translate('n/a');
    }

    return this.getValue((dayOldChicksPrice / amountOfChicks) * 100);
  }

  //#endregion Financial

  aggregateDayOldChicks(dayOldChicks: DayOldChick[]): DayOldChick[] {
    const aggregatedDayOldChicks: DayOldChick[] = [];

    dayOldChicks.forEach((d) => {
      const existing = aggregatedDayOldChicks.find((ad) => ad.arrivalDate === d.arrivalDate);

      if (existing) {
        existing.amountOfChicks += d.amountOfChicks;
        existing.chicksTotalPrice += Number.isFinite(d.chicksPrice)
          ? d.amountOfChicks * d.chicksPrice
          : undefined;
      } else {
        aggregatedDayOldChicks.push({
          ...d,
          chicksTotalPrice: Number.isFinite(d.chicksPrice)
            ? d.amountOfChicks * d.chicksPrice
            : undefined,
        });
      }
    });

    return aggregatedDayOldChicks;
  }

  iOS(): boolean {
    return (
      ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(
        // eslint-disable-next-line
        navigator.platform,
      ) ||
      // iPad on iOS 13 detection
      // eslint-disable-next-line
      (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
    );
  }
}
